import { ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import Table, { Header, Row } from 'src/components/Table';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  PaginatedResponse,
  WorkflowExecutionThreadFindAllFilters,
  WorkflowExecutionThreadWithComputedStatus,
} from 'src/interfaces/workflows.interface';
import { useCrmSync } from 'src/pages/Automations/contexts/CrmSyncContext';
import { CrmSyncProviders } from 'src/pages/Automations/contexts/CrmSyncProviders';
import { extractDigits } from 'src/utils/number';
import { useWizard } from '../../../components/wizard/WizardContext';

type ContactRow = {
  id: string;
  toNumber: string;
  contactName: string;
  source: string;
  dateAdded: string;
};

function ContactsContainer({
  onEditCrmIntegration,
  isEditing,
}: {
  onEditCrmIntegration: () => void;
  isEditing: boolean;
}) {
  const { isInitializing, saveCampaignAutomation, campaignAutomation } =
    useWizard();
  const { id: campaignReferenceId } = useParams();
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const { integratedCrm } = useCrmSync();
  const isCrmSynced = !!integratedCrm;

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [contacts, setContacts] = useState<ContactRow[]>([]);

  const headers: Header[] = useMemo(
    () => [
      {
        key: 'toNumber',
        label: 'To number',
        width: '25%',
        disableSorting: true,
      },
      {
        key: 'contactName',
        label: 'Contact name',
        width: '25%',
        disableSorting: true,
      },
      { key: 'source', label: 'Source', width: '25%', disableSorting: true },
      {
        key: 'dateAdded',
        label: 'Date added',
        width: '25%',
        disableSorting: true,
      },
    ],
    [],
  );

  const fetchContacts = useCallback(async () => {
    if (!campaignReferenceId) return;

    setLoading(true);
    try {
      const queryFilters: WorkflowExecutionThreadFindAllFilters = {
        workflowReferenceId: campaignReferenceId,
      };

      const numberDigitsOnly = extractDigits(searchQuery || '');
      if (numberDigitsOnly.length > 0) {
        queryFilters.executionParamKey = 'to_number';
        queryFilters.executionParamValue = numberDigitsOnly;
      }

      const queryObject: QueryObject = {
        page: String(currentPage),
        size: String(currentPageSize),
        filters: JSON.stringify(queryFilters),
        sort_column: 'thread_created_at',
        sort_desc: 'true',
      };

      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
        await get(`/workflow_threads?${query.toString()}`);

      const mapped: ContactRow[] = data.items.map((thread) => ({
        id: thread.id,
        toNumber: (thread.execution_params?.to_number as string) || 'N/A',
        contactName: (thread.execution_params?.contact_name as string) || '',
        source: '—',
        dateAdded: new Date(thread.created_at).toLocaleDateString(),
      }));

      setContacts(mapped);
      setTotalItems(data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [campaignReferenceId, currentPage, currentPageSize, searchQuery, get]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const rows: Row[] = contacts.map((c) => ({
    toNumber: c.toNumber,
    contactName: c.contactName,
    source: c.source,
    dateAdded: c.dateAdded,
  }));

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  const saveAndRedirectTo = async (to: 'csv' | 'crm' = 'csv') => {
    const campaignSaved = await saveCampaignAutomation();
    if (campaignSaved)
      navigate(
        `/beta/campaigns/${campaignAutomation?.referenceId}/contacts/${to}`,
      );
  };

  const handleAddContactsFromCsv = () => {
    if (!isEditing) {
      saveAndRedirectTo('csv');
      return;
    }

    if (!campaignReferenceId) return;
    navigate(`/beta/campaigns/${campaignReferenceId}/contacts/csv`);
  };

  const handleAddContactsFromCrm = () => {
    if (!isEditing) {
      saveAndRedirectTo('crm');
      return;
    }

    if (!campaignReferenceId) return;
    navigate(`/beta/campaigns/${campaignReferenceId}/contacts/crm`);
  };

  const handleSetUpCrmIntegration = () => {
    if (!isEditing) {
      saveAndRedirectTo('crm');
      return;
    }

    if (!campaignReferenceId) return;
    onEditCrmIntegration();
  };

  return (
    <div className="p-4">
      <div className="relative">
        {isInitializing ? (
          <div className="flex gap-2 items-center justify-center py-12">
            <Loading size="8" />
            <p className="text-neutral-400">Loading...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {contacts.length > 0 && !loading && (
              <div className="flex">
                <div className="w-full md:w-96">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by phone number"
                    icon={
                      <MagnifyingGlass size={20} className="text-neutral-400" />
                    }
                    iconPosition="left"
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') fetchContacts();
                    }}
                    inputAction={() => fetchContacts()}
                  />
                </div>
              </div>
            )}

            {contacts.length === 0 && !loading ? (
              <div className="relative py-16">
                {/* Background table skeleton */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                  <div className="h-full w-full rounded-lg border border-neutral-100 bg-neutral-0 overflow-hidden">
                    {/* Header */}
                    <div className="grid grid-cols-4 gap-6 text-sm text-neutral-500 font-medium px-6 py-4 border-b border-neutral-100">
                      <div>To number</div>
                      <div>Contact name</div>
                      <div>Source</div>
                      <div>Date added</div>
                    </div>
                    {/* Rows */}
                    <div className="px-6 py-4 space-y-6">
                      {Array.from({ length: 1 }).map((_, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-4 gap-6 items-center"
                        >
                          <div className="h-5 bg-neutral-100 rounded w-28" />
                          <div className="h-5 bg-neutral-100 rounded w-40" />
                          <div className="h-5 bg-neutral-100 rounded w-24" />
                          <div className="h-5 bg-neutral-100 rounded w-28" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Table
                headers={headers}
                rows={rows}
                totalItems={totalItems}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                loading={loading}
                setSize={handleSizeChange}
                defaultSize={currentPageSize}
                onSort={() => {}}
                disablePagination={totalItems <= currentPageSize}
              />
            )}

            <AddContactsContent
              handleAddContactsFromCsv={handleAddContactsFromCsv}
              handleAddContactsFromCrm={handleAddContactsFromCrm}
              handleSetUpCrmIntegration={handleSetUpCrmIntegration}
              hasRows={contacts.length > 0}
              isCrmSynced={isCrmSynced}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const AddContactsContent = ({
  handleAddContactsFromCsv,
  handleAddContactsFromCrm,
  handleSetUpCrmIntegration,
  hasRows,
  isCrmSynced,
}: {
  handleAddContactsFromCsv: () => void;
  handleAddContactsFromCrm: () => void;
  handleSetUpCrmIntegration: () => void;
  hasRows: boolean;
  isCrmSynced: boolean;
}) => {
  return (
    <div className="relative z-10 mt-24">
      <h2 className="text-center text-2xl font-semibold text-neutral-900">
        {hasRows ? 'Add more contacts' : 'Choose how to get started'}
      </h2>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        <button
          type="button"
          onClick={handleAddContactsFromCsv}
          className="w-full text-left rounded-lg border border-neutral-200 bg-neutral-0 hover:bg-neutral-50 transition shadow-sm"
        >
          <div className="p-6 flex items-center justify-between">
            <div>
              <div className="font-medium text-neutral-900">Upload a CSV</div>
              <div className="text-sm text-neutral-500 mt-1">
                Add contacts using a CSV file
              </div>
            </div>
            <ArrowRight size={20} className="text-neutral-400" />
          </div>
        </button>

        {isCrmSynced && (
          <button
            type="button"
            onClick={handleAddContactsFromCrm}
            className="w-full text-left rounded-lg border border-neutral-200 bg-neutral-0 hover:bg-neutral-50 transition shadow-sm"
          >
            <div className="p-6 flex items-center justify-between">
              <div>
                <div className="font-medium text-neutral-900">
                  Sync from CRM
                </div>
                <div className="text-sm text-neutral-500 mt-1">
                  Import contacts directly from your CRM
                </div>
              </div>
              <ArrowRight size={20} className="text-neutral-400" />
            </div>
          </button>
        )}

        {!isCrmSynced && (
          <button
            type="button"
            onClick={handleSetUpCrmIntegration}
            className="w-full text-left rounded-lg border border-neutral-200 bg-neutral-0 hover:bg-neutral-50 transition shadow-sm"
          >
            <div className="p-6 flex items-center justify-between">
              <div>
                <div className="font-medium text-neutral-900">
                  Set up CRM integration
                </div>
                <div className="text-sm text-neutral-500 mt-1">
                  Set up CRM integration to automatically sync contacts into
                  this campaign.
                </div>
              </div>
              <ArrowRight size={20} className="text-neutral-400" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default function ContactsContent({
  onEditCrmIntegration = () => {},
  isEditing = true,
}: {
  onEditCrmIntegration?: () => void;
  isEditing?: boolean;
}) {
  return (
    <CrmSyncProviders>
      <ContactsContainer
        onEditCrmIntegration={onEditCrmIntegration}
        isEditing={isEditing}
      />
    </CrmSyncProviders>
  );
}
